import html2canvas from "html2canvas";
import katex from "katex";
import "katex/dist/katex.min.css";

/**
 * Reemplaza expresiones LaTeX en un texto con placeholders
 */
function replaceLatexWithPlaceholders(text: string): {
  text: string;
  expressions: Map<string, string>;
} {
  const expressions = new Map<string, string>();
  let placeholderIndex = 0;

  const textWithPlaceholders = text.replace(/\$([^$]+)\$/g, (match, latex) => {
    const placeholder = `__LATEX_PLACEHOLDER_${placeholderIndex}__`;
    expressions.set(placeholder, latex);
    placeholderIndex++;
    return placeholder;
  });

  return {
    text: textWithPlaceholders,
    expressions,
  };
}

/**
 * Renderiza una expresión LaTeX a HTML
 */
function renderLatexToHTML(latex: string): string {
  try {
    return katex.renderToString(latex, {
      throwOnError: false,
      displayMode: false,
    });
  } catch (error) {
    console.error("Error renderizando LaTeX:", error);
    return `<span>${latex}</span>`;
  }
}

/**
 * Convierte un elemento HTML a imagen (canvas)
 */
async function elementToImage(element: HTMLElement): Promise<string> {
  const canvas = await html2canvas(element, {
    backgroundColor: null,
    scale: 2, // Mayor resolución
  });
  return canvas.toDataURL("image/png");
}

/**
 * Renderiza texto con LaTeX a una imagen que puede ser insertada en PDF
 */
export async function renderTextWithLatexToImage(
  text: string,
  maxWidth: number = 500
): Promise<string> {
  // Si no hay LaTeX, retornar null para usar texto normal
  if (!text.includes("$")) {
    return "";
  }

  const { text: textWithPlaceholders, expressions } =
    replaceLatexWithPlaceholders(text);

  // Crear contenedor temporal
  const container = document.createElement("div");
  container.style.position = "absolute";
  container.style.left = "-9999px";
  container.style.top = "-9999px";
  container.style.width = `${maxWidth}px`;
  container.style.padding = "10px";
  container.style.fontSize = "14px";
  container.style.fontFamily = "Arial, sans-serif";
  container.style.color = "#000000";
  container.style.backgroundColor = "#ffffff";
  container.style.lineHeight = "1.5";

  // Dividir el texto en partes y renderizar
  const placeholderRegex = /(__LATEX_PLACEHOLDER_\d+__)/;
  const parts = textWithPlaceholders.split(placeholderRegex);

  parts.forEach((part) => {
    if (part.startsWith("__LATEX_PLACEHOLDER_") && part.endsWith("__")) {
      // Es un placeholder de LaTeX
      const latex = expressions.get(part);
      if (latex) {
        const span = document.createElement("span");
        span.innerHTML = renderLatexToHTML(latex);
        span.style.display = "inline-block";
        span.style.verticalAlign = "middle";
        span.style.marginLeft = "2px";
        span.style.marginRight = "2px";
        container.appendChild(span);
      }
    } else if (part) {
      // Es texto normal
      const textNode = document.createTextNode(part);
      container.appendChild(textNode);
    }
  });

  document.body.appendChild(container);

  try {
    const imageDataUrl = await elementToImage(container);
    document.body.removeChild(container);
    return imageDataUrl;
  } catch (error) {
    console.error("Error convirtiendo LaTeX a imagen:", error);
    document.body.removeChild(container);
    return "";
  }
}

/**
 * Procesa un texto y retorna información sobre si contiene LaTeX y cómo renderizarlo
 */
export async function processTextForPDF(text: string): Promise<{
  hasLatex: boolean;
  imageDataUrl?: string;
  plainText?: string;
}> {
  if (!text.includes("$")) {
    return {
      hasLatex: false,
      plainText: text,
    };
  }

  const imageDataUrl = await renderTextWithLatexToImage(text);
  return {
    hasLatex: true,
    imageDataUrl,
  };
}
